import { waGatewayHost, apiHost } from '../api'
import { showNotification } from './notifications'

// Hardcoded for now — CRA bakes env vars at startup and won't pick up
// .env changes without a restart.  Move to env var for production.
const WA_API_KEY = '78fb6eea62d57efe25efe98c112a6264'

// Only connect to WA gateway on staging/business environments.
// Comment out the next line to enable on localhost for development.
const WA_ENABLED_HOSTS = [
  'staging.rolzo.com',
  'business.rolzo.com',
  // 'localhost',
]
const isWAEnabled = () => WA_ENABLED_HOSTS.includes(window.location.hostname)

export const WA_SET_CONVERSATIONS = 'WA_SET_CONVERSATIONS'
export const WA_APPEND_CONVERSATIONS = 'WA_APPEND_CONVERSATIONS'
export const WA_MERGE_CONVERSATIONS = 'WA_MERGE_CONVERSATIONS'
export const WA_SET_LOADING_MORE = 'WA_SET_LOADING_MORE'
export const WA_SET_MESSAGES = 'WA_SET_MESSAGES'
export const WA_SET_SELECTED = 'WA_SET_SELECTED'
export const WA_APPEND_MESSAGE = 'WA_APPEND_MESSAGE'
export const WA_SET_LOADING = 'WA_SET_LOADING'
export const WA_SET_UNREAD_COUNT = 'WA_SET_UNREAD_COUNT'
export const WA_UPDATE_CONVERSATION = 'WA_UPDATE_CONVERSATION'
export const WA_SET_SESSIONS = 'WA_SET_SESSIONS'
export const WA_SET_SELECTED_SESSION = 'WA_SET_SELECTED_SESSION'
export const WA_SET_SESSION_QR = 'WA_SET_SESSION_QR'
export const WA_SET_SESSION_MANAGER_OPEN = 'WA_SET_SESSION_MANAGER_OPEN'
export const WA_UPDATE_SESSION = 'WA_UPDATE_SESSION'
export const WA_WS_CONNECTED = 'WA_WS_CONNECTED'
export const WA_WS_DISCONNECTED = 'WA_WS_DISCONNECTED'
export const WA_UPDATE_MESSAGE_ACK = 'WA_UPDATE_MESSAGE_ACK'
export const WA_REVOKE_MESSAGE = 'WA_REVOKE_MESSAGE'
export const WA_UPDATE_MESSAGE = 'WA_UPDATE_MESSAGE'
export const WA_SET_CHANNEL_UNREADS = 'WA_SET_CHANNEL_UNREADS'
export const WA_SET_ACTIVE_CHANNEL = 'WA_SET_ACTIVE_CHANNEL'
export const WA_SET_TAB_COUNTS = 'WA_SET_TAB_COUNTS'

export const setWAActiveChannel = channel => ({
  type: WA_SET_ACTIVE_CHANNEL,
  payload: channel,
})

/**
 * Direct fetch to WA gateway — bypasses ApiService.handleResponse
 * since the gateway returns { data, pagination } without meta.success wrapper.
 */
const waFetch = async (url, options = {}) => {
  if (!isWAEnabled()) return {}
  const resp = await fetch(`${waGatewayHost}/${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': WA_API_KEY,
      ...(options.headers || {}),
    },
  })
  return resp.json()
}

// Build the query string for /chats. Reused by fetch / loadMore / merge.
const buildWAChatsQuery = (params = {}) => {
  const query = new URLSearchParams()
  if (params.sessionId) query.set('sessionId', params.sessionId)
  if (params.channel) query.set('channel', params.channel)
  if (params.status === 'blocked') {
    query.set('blocked', 'true')
  } else {
    if (params.status) query.set('status', params.status)
  }
  if (params.inbox) query.set('inbox', params.inbox)
  if (params.search) query.set('search', params.search)
  if (params.page) query.set('page', params.page)
  if (params.limit) query.set('limit', params.limit || 30)
  return query
}

export const fetchWAConversations = (params = {}) => async dispatch => {
  try {
    dispatch({ type: WA_SET_LOADING, payload: true })

    const query = buildWAChatsQuery(params)
    const result = await waFetch(`chats?${query.toString()}`)

    const conversations = result.data || []
    const pagination = result.pagination || {}
    const counts = result.counts || null

    dispatch({
      type: WA_SET_CONVERSATIONS,
      payload: { conversations, pagination },
    })
    if (counts) {
      dispatch({ type: WA_SET_TAB_COUNTS, payload: counts })
    }

    // Calculate total unread
    const totalUnread = conversations.reduce(
      (sum, c) => sum + (c.unreadCount || 0),
      0
    )
    dispatch({ type: WA_SET_UNREAD_COUNT, payload: totalUnread })
  } catch (err) {
    console.error('fetchWAConversations error:', err)
  } finally {
    dispatch({ type: WA_SET_LOADING, payload: false })
  }
}

// Load the next page of conversations, appending to the existing list.
// Safe to call repeatedly: bails if already loading or nothing more to fetch.
export const loadMoreWAConversations = (extraParams = {}) => async (
  dispatch,
  getState
) => {
  const state = getState().whatsapp
  if (!state) return
  if (state.loadingMore) return
  const { pagination = {} } = state
  const currentPage = pagination.page || 1
  const totalPages = pagination.pages || 1
  if (currentPage >= totalPages) return

  try {
    dispatch({ type: WA_SET_LOADING_MORE, payload: true })
    const query = buildWAChatsQuery({
      ...extraParams,
      page: currentPage + 1,
      limit: pagination.limit || extraParams.limit || 50,
    })
    const result = await waFetch(`chats?${query.toString()}`)
    const conversations = result.data || []
    const nextPagination = result.pagination || {}
    const counts = result.counts || null
    dispatch({
      type: WA_APPEND_CONVERSATIONS,
      payload: { conversations, pagination: nextPagination },
    })
    if (counts) {
      dispatch({ type: WA_SET_TAB_COUNTS, payload: counts })
    }
  } catch (err) {
    console.error('loadMoreWAConversations error:', err)
  } finally {
    dispatch({ type: WA_SET_LOADING_MORE, payload: false })
  }
}

// Fetch page 1 and upsert into existing list. Used by the fallback poller so
// pagination state is preserved when the user has scrolled past page 1.
export const mergeWAConversations = (params = {}) => async dispatch => {
  try {
    const query = buildWAChatsQuery({ ...params, page: 1 })
    const result = await waFetch(`chats?${query.toString()}`)
    const conversations = result.data || []
    const pagination = result.pagination || {}
    const counts = result.counts || null
    dispatch({
      type: WA_MERGE_CONVERSATIONS,
      payload: { conversations, pagination },
    })
    if (counts) {
      dispatch({ type: WA_SET_TAB_COUNTS, payload: counts })
    }
  } catch (err) {
    console.error('mergeWAConversations error:', err)
  }
}

export const fetchWAUnreadCounts = () => async dispatch => {
  try {
    const result = await waFetch('unread-counts')
    dispatch({
      type: WA_SET_CHANNEL_UNREADS,
      payload: result.data || { client: 0, partner: 0 },
    })
  } catch (err) {
    console.error('fetchWAUnreadCounts error:', err)
  }
}

export const fetchWAMessages = (conversationId, page = 1) => async dispatch => {
  try {
    const result = await waFetch(
      `chats/${conversationId}/messages?page=${page}&limit=30`
    )

    const messages = result.data || []
    const pagination = result.pagination || {}

    dispatch({
      type: WA_SET_MESSAGES,
      payload: {
        messages,
        pagination,
        page,
      },
    })
  } catch (err) {
    console.error('fetchWAMessages error:', err)
  }
}

export const sendWAMessage = (
  chatId,
  body,
  agent,
  agentId,
  replyData,
  agentImage,
  mediaData,
  sessionId
) => async dispatch => {
  try {
    const payload = { chatId, body, agent, agentId, agentImage }
    if (sessionId) payload.sessionId = sessionId
    if (mediaData) {
      payload.mediaUrl = mediaData.url
      payload.mimetype = mediaData.mimetype
      payload.filename = mediaData.filename
    }
    if (replyData) {
      payload.quotedId = replyData.waMessageId
      payload.replyPreview = {
        text: replyData.previewText || null,
        author: replyData.previewAuthor || null,
        type: replyData.previewType || null,
        messageId: replyData.messageId || null,
      }
    }
    const result = await waFetch('send', {
      method: 'POST',
      body: JSON.stringify(payload),
    })

    // Message arrives via WebSocket (wa:message event from message_create handler)
    return result
  } catch (err) {
    console.error('sendWAMessage error:', err)
    throw err
  }
}

// Partner channel: ask the gateway whether this agent may reply to this group.
// Returns { canReply, reason } — fails open so a network blip never hard-blocks
// the composer (the /send call is the real gate).
export const fetchWACanReply = async (chatId, agentId) => {
  try {
    const query = agentId ? `?agentId=${encodeURIComponent(agentId)}` : ''
    const result = await waFetch(`chats/${chatId}/can-reply${query}`)
    return (result && result.data) || { canReply: true, reason: 'OK' }
  } catch (err) {
    console.error('fetchWACanReply error:', err)
    return { canReply: true, reason: 'OK' }
  }
}

export const updateWAConversation = (id, data) => async dispatch => {
  try {
    const result = await waFetch(`chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })

    const updated = result.data || {}

    dispatch({
      type: WA_UPDATE_CONVERSATION,
      payload: updated,
    })

    return updated
  } catch (err) {
    console.error('updateWAConversation error:', err)
    throw err
  }
}

export const setSelectedWAConversation = conversation => ({
  type: WA_SET_SELECTED,
  payload: conversation,
})

export const fetchWASessions = channel => async dispatch => {
  try {
    let url = 'admin/sessions'
    if (channel) url += `?channel=${channel}`
    const result = await waFetch(url)
    const sessions = result.data || []
    dispatch({ type: WA_SET_SESSIONS, payload: sessions })
    return sessions
  } catch (err) {
    console.error('fetchWASessions error:', err)
    return []
  }
}

export const setSelectedWASession = session => ({
  type: WA_SET_SELECTED_SESSION,
  payload: session,
})

export const setSessionManagerOpen = open => ({
  type: WA_SET_SESSION_MANAGER_OPEN,
  payload: open,
})

export const fetchWASessionQR = sessionId => async dispatch => {
  try {
    const result = await waFetch(`admin/sessions/${sessionId}/qr`)
    const qr = result.qrCode || null
    dispatch({ type: WA_SET_SESSION_QR, payload: { sessionId, qr } })
    return qr
  } catch (err) {
    console.error('fetchWASessionQR error:', err)
    return null
  }
}

export const connectWASession = sessionId => async (dispatch, getState) => {
  try {
    await waFetch(`admin/sessions/${sessionId}/connect`, { method: 'POST' })
    await dispatch(fetchWASessions(getState().whatsapp.activeChannel))
  } catch (err) {
    console.error('connectWASession error:', err)
  }
}

export const disconnectWASession = sessionId => async (dispatch, getState) => {
  try {
    await waFetch(`admin/sessions/${sessionId}/disconnect`, { method: 'POST' })
    await dispatch(fetchWASessions(getState().whatsapp.activeChannel))
  } catch (err) {
    console.error('disconnectWASession error:', err)
  }
}

export const reconnectWASession = sessionId => async (dispatch, getState) => {
  try {
    await waFetch(`admin/sessions/${sessionId}/reconnect`, { method: 'POST' })
    await dispatch(fetchWASessions(getState().whatsapp.activeChannel))
  } catch (err) {
    console.error('reconnectWASession error:', err)
  }
}

export const logoutWASession = sessionId => async (dispatch, getState) => {
  try {
    await waFetch(`admin/sessions/${sessionId}/logout`, { method: 'POST' })
    dispatch({ type: WA_SET_SESSION_QR, payload: { sessionId, qr: null } })
    await dispatch(fetchWASessions(getState().whatsapp.activeChannel))
  } catch (err) {
    console.error('logoutWASession error:', err)
  }
}

export const createWASession = (
  sessionId,
  label,
  channel,
  extra = {}
) => async (dispatch, getState) => {
  try {
    // Auto-derive the proxy country from the logged-in operator's IP (the real
    // ISO alpha-2 from CF-IPCountry, not the bucketed ipLocation). The gateway
    // uses it to geo-match an idle proxy when its pool is enabled; an explicit
    // country in `extra` still wins. Omitted when unknown (e.g. local dev with
    // no Cloudflare header) — the gateway only requires it when pooling is on.
    const ipCountryCode = getState().auth.ipCountryCode
    const country =
      extra.country ||
      (/^[A-Za-z]{2}$/.test(ipCountryCode || '') ? ipCountryCode : undefined)

    // extra carries partner-channel fields { role, agentUserId } — omitted for client.
    const result = await waFetch('admin/sessions', {
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        label,
        channel,
        ...extra,
        ...(country ? { country } : {}),
      }),
    })
    await dispatch(fetchWASessions(getState().whatsapp.activeChannel))
    return result
  } catch (err) {
    console.error('createWASession error:', err)
    throw err
  }
}

export const deleteWASession = sessionId => async (dispatch, getState) => {
  try {
    await waFetch(`admin/sessions/${sessionId}`, { method: 'DELETE' })
    await dispatch(fetchWASessions(getState().whatsapp.activeChannel))
  } catch (err) {
    console.error('deleteWASession error:', err)
    throw err
  }
}

export const updateWASession = (sessionId, data) => async dispatch => {
  try {
    const result = await waFetch(`admin/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    if (result.data) dispatch({ type: WA_UPDATE_SESSION, payload: result.data })
    return result
  } catch (err) {
    console.error('updateWASession error:', err)
    throw err
  }
}

export const syncWAChats = (sessionId, channel) => async dispatch => {
  try {
    const query = sessionId ? `?sessionId=${sessionId}` : ''
    const result = await waFetch(`chats/sync${query}`)
    await dispatch(
      fetchWAConversations({ limit: 50, ...(channel && { channel }) })
    )

    if (result.error) {
      dispatch(showNotification({ message: result.error, type: 'error' }))
    } else {
      const label = result.label || result.sessionId || 'WhatsApp'
      dispatch(
        showNotification({
          message: `${label}: Synced ${result.synced} chats (${result.created} new, ${result.updated} updated)`,
        })
      )
    }
  } catch (err) {
    console.error('syncWAChats error:', err)
    dispatch(
      showNotification({
        message: 'Sync failed — check connection',
        type: 'error',
      })
    )
  }
}

export const markWAUrgent = (
  id,
  isUrgent,
  agent,
  agentId
) => async dispatch => {
  try {
    const result = await waFetch(`chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isUrgent, agent, agentId }),
    })
    dispatch({ type: WA_UPDATE_CONVERSATION, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('markWAUrgent error:', err)
  }
}

export const assignWAType = (
  id,
  typeAssigned,
  agent,
  agentId
) => async dispatch => {
  try {
    const result = await waFetch(`chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ typeAssigned, agent, agentId }),
    })
    dispatch({ type: WA_UPDATE_CONVERSATION, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('assignWAType error:', err)
  }
}

export const updateWAStatus = (id, data, agent, agentId) => async dispatch => {
  try {
    const result = await waFetch(`chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...data, agent, agentId }),
    })
    dispatch({ type: WA_UPDATE_CONVERSATION, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('updateWAStatus error:', err)
  }
}

export const addWAAdminNote = (id, noteData) => async dispatch => {
  try {
    const result = await waFetch(`chats/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify(noteData),
    })
    dispatch({ type: WA_UPDATE_CONVERSATION, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('addWAAdminNote error:', err)
  }
}

export const editWAAdminNote = (
  conversationId,
  noteId,
  data
) => async dispatch => {
  try {
    const result = await waFetch(`chats/${conversationId}/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    dispatch({ type: WA_UPDATE_CONVERSATION, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('editWAAdminNote error:', err)
  }
}

export const fetchWALogs = conversationId => async () => {
  try {
    const result = await waFetch(`chats/${conversationId}/logs`)
    return result.data || []
  } catch (err) {
    console.error('fetchWALogs error:', err)
    return []
  }
}

export const deleteWAAdminNote = (conversationId, noteId) => async dispatch => {
  try {
    const result = await waFetch(`chats/${conversationId}/notes/${noteId}`, {
      method: 'DELETE',
    })
    dispatch({ type: WA_UPDATE_CONVERSATION, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('deleteWAAdminNote error:', err)
  }
}

export const searchWAClients = (query, companyType = 'client') => async () => {
  try {
    const response = await fetch(
      `${apiHost}/api/v1/company/companyOptions/${companyType}/${encodeURIComponent(
        query
      )}`
    )
    const result = await response.json()
    return result?.data?.companyOptions || result?.companyOptions || []
  } catch (err) {
    console.error('searchWAClients error:', err)
    return []
  }
}

export const editWAMessage = (
  messageId,
  body,
  agent,
  agentId
) => async dispatch => {
  try {
    const result = await waFetch(`messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body, agent, agentId }),
    })
    if (result.data) dispatch({ type: WA_UPDATE_MESSAGE, payload: result.data })
    return result
  } catch (err) {
    console.error('editWAMessage error:', err)
    throw err
  }
}

export const deleteWAMessageForMe = (
  messageId,
  agent,
  agentId
) => async dispatch => {
  try {
    const result = await waFetch(`messages/${messageId}?mode=forMe`, {
      method: 'DELETE',
      body: JSON.stringify({ agent, agentId }),
    })
    if (result.data) dispatch({ type: WA_UPDATE_MESSAGE, payload: result.data })
    return result
  } catch (err) {
    console.error('deleteWAMessageForMe error:', err)
    throw err
  }
}

export const deleteWAMessageForEveryone = (
  messageId,
  agent,
  agentId
) => async dispatch => {
  try {
    const result = await waFetch(`messages/${messageId}?mode=forEveryone`, {
      method: 'DELETE',
      body: JSON.stringify({ agent, agentId }),
    })
    if (result.error) return result
    if (result.data) dispatch({ type: WA_REVOKE_MESSAGE, payload: result.data })
    return result
  } catch (err) {
    console.error('deleteWAMessageForEveryone error:', err)
    throw err
  }
}

export const fetchWAMessageInfo = messageId => async () => {
  try {
    const result = await waFetch(`messages/${messageId}/info`)
    if (result.error) return { error: result.error }
    return result.data || {}
  } catch (err) {
    console.error('fetchWAMessageInfo error:', err)
    return { error: 'Failed to fetch message info' }
  }
}

export const uploadWAFile = file => async () => {
  const formData = new FormData()
  formData.append('file', file)

  const resp = await fetch(`${waGatewayHost}/upload`, {
    method: 'POST',
    headers: { 'x-api-key': WA_API_KEY },
    body: formData,
  })
  const result = await resp.json()
  if (!result.success) throw new Error(result.error || 'Upload failed')
  return result.data
}

// ── WebSocket connection ──────────────────────────────

let waSocket = null
let waReconnectTimer = null

const getWsUrl = () => {
  const wsProto = waGatewayHost.replace(/^http/, 'ws')
  return `${wsProto}/ws?apiKey=${WA_API_KEY}`
}

export const connectWAWebSocket = () => (dispatch, getState) => {
  if (!isWAEnabled()) return
  if (waSocket && waSocket.readyState <= 1) return // already open/connecting

  const ws = new WebSocket(getWsUrl())
  waSocket = ws

  ws.onopen = () => {
    dispatch({ type: WA_WS_CONNECTED })
    if (waReconnectTimer) {
      clearInterval(waReconnectTimer)
      waReconnectTimer = null
    }
  }

  ws.onclose = () => {
    dispatch({ type: WA_WS_DISCONNECTED })
    waSocket = null
    // Auto-reconnect every 5s
    if (!waReconnectTimer) {
      waReconnectTimer = setInterval(() => {
        dispatch(connectWAWebSocket())
      }, 5000)
    }
  }

  ws.onerror = () => {
    // onclose will fire after onerror
  }

  ws.onmessage = event => {
    try {
      const msg = JSON.parse(event.data)
      dispatch(handleWAWebSocketEvent(msg))
    } catch (err) {
      console.error('WA WS parse error:', err)
    }
  }
}

export const disconnectWAWebSocket = () => () => {
  if (waReconnectTimer) {
    clearInterval(waReconnectTimer)
    waReconnectTimer = null
  }
  if (waSocket) {
    waSocket.onclose = null // prevent auto-reconnect on intentional close
    waSocket.close()
    waSocket = null
  }
}

const handleWAWebSocketEvent = msg => (dispatch, getState) => {
  const { whatsapp } = getState()

  switch (msg.event) {
    case 'wa:message': {
      const { message, conversationId, channel: msgChannel } = msg.data
      const activeChannel = getState().whatsapp.activeChannel
      // Skip messages for conversations that don't match the current channel
      if (activeChannel && msgChannel && msgChannel !== activeChannel) {
        break
      }
      // Only append if this conversation is currently selected
      if (
        whatsapp.selectedConversation &&
        whatsapp.selectedConversation._id === conversationId
      ) {
        // Dedup by _id or waMessageId
        const exists = whatsapp.messages.some(
          m =>
            m._id === message._id ||
            (m.waMessageId && m.waMessageId === message.waMessageId)
        )
        if (!exists) {
          if (
            message.fromMe &&
            (!message.sentByAgent || !message.sentByAgentImage)
          ) {
            const { auth } = getState()
            const agentName = `${auth.firstName || ''} ${auth.lastName ||
              ''}`.trim()
            if (!message.sentByAgent && agentName) {
              message.sentByAgent = agentName
            }
            if (!message.sentByAgentImage && auth.image) {
              message.sentByAgentImage = auth.image
            }
          }
          dispatch({ type: WA_APPEND_MESSAGE, payload: message })
        }
      }
      break
    }

    case 'wa:message:ack': {
      dispatch({ type: WA_UPDATE_MESSAGE_ACK, payload: msg.data })
      break
    }

    case 'wa:message:revoke': {
      dispatch({ type: WA_REVOKE_MESSAGE, payload: msg.data })
      break
    }

    case 'wa:message:update': {
      const { message, conversationId } = msg.data
      // Update message in current view (e.g. media URL now available)
      if (
        whatsapp.selectedConversation &&
        whatsapp.selectedConversation._id === conversationId
      ) {
        dispatch({ type: WA_UPDATE_MESSAGE, payload: message })
      }
      break
    }

    case 'wa:conversation:update': {
      const activeChannel = getState().whatsapp.activeChannel
      // Skip updates for conversations that don't match the current channel
      if (
        activeChannel &&
        msg.data.channel &&
        msg.data.channel !== activeChannel
      ) {
        break
      }
      dispatch({ type: WA_UPDATE_CONVERSATION, payload: msg.data })
      // Recalculate total unread from all conversations
      const convs = getState().whatsapp.conversations
      const totalUnread = convs.reduce(
        (sum, c) =>
          sum +
          (c._id === msg.data._id
            ? msg.data.unreadCount || 0
            : c.unreadCount || 0),
        0
      )
      dispatch({ type: WA_SET_UNREAD_COUNT, payload: totalUnread })
      dispatch(fetchWAUnreadCounts())
      break
    }

    case 'wa:session:update': {
      dispatch({ type: WA_UPDATE_SESSION, payload: msg.data })
      break
    }

    case 'wa:sync:complete': {
      const { label, chatsProcessed, newMessages } = msg.data
      dispatch(
        showNotification({
          message: `${label ||
            'WhatsApp'}: Sync complete — ${chatsProcessed} chats, ${newMessages} new messages`,
        })
      )
      // Refresh conversations scoped to the active channel
      const syncChannel = getState().whatsapp.activeChannel
      dispatch(
        fetchWAConversations({
          limit: 50,
          ...(syncChannel && { channel: syncChannel }),
        })
      )
      break
    }

    default:
      break
  }
}
